import { Checkbox, Chip } from "@mui/material";
import { useSelector } from "react-redux";
import React, { useState } from "react";
import {
	CategoryRowRiskLevel,
	CustomIconButton,
	CustomTooltip,
	Hierarchy,
	icon,
	IconComponent,
	VerticalDivider,
} from "../..";
import { isSegFeatureEnabled, SegFlags, translate } from "../../../providers";
import { dangerousSetInnerHTML, isNonEmptyArray } from "../../../utils";
import styles from "./SearchRow.module.css";
import { SENTENCE_TYPE } from "../../../../views/my-review/constants/constants";
import { useApi } from "../../../hooks";
import { DocumentService, ProjectDirectoryService } from "../../../../api";
import { NavUtils } from "../../../../navigation";

const DISPLAY_SCORE = false;
export default function SearchRow({
	categories,
	content,
	coverageStatus,
	documentId,
	documentName,
	hasFolderIcon,
	informationId,
	isCheckboxDisplayed,
	isChecked,
	covered,
	isTemplate,
	location,
	onCheck,
	onDisplay,
	page,
	row,
	rowProjectId,
	score,
	informationType,
}) {
	const [hierarchy, setHierarchy] = useState("");
	const [folderId, setFolderId] = useState("");
	const { call: getDocumentDetails } = useApi(DocumentService.getInformation);
	const { call: getLocation } = useApi(ProjectDirectoryService.getLocation);
	const projectId = useSelector(({ context }) => context?.project?.id);
	const handleClickDisplay = () => {
		onDisplay({
			informationId,
			documentId,
			documentName,
			location,
			page,
			informationType,
			coordinates: row?.rectangleDTO || null,
			projectId: rowProjectId,
		});
	};
	const handleRetrieveHierarchy = () => {
		if (!hierarchy) {
			getDocumentDetails({ docId: documentId })
				.then(({ directoryId }) => {
					const dirId = (!directoryId && "root") || directoryId;
					setFolderId(dirId);
					getLocation({ projectId: rowProjectId || projectId, directoryId: dirId })
						.then((data) => {
							if (isNonEmptyArray(data)) {
								const constructedHierarchy = data.map(({ label }) => `${label}/`);
								setHierarchy(`/${constructedHierarchy}${documentName}`);
							} else {
								setHierarchy("/");
							}
						})
						.catch(console.error);
				})
				.catch(console.error);
		}
	};
	const handleOpenDocumentCenter = () => {
		if (folderId) {
			window.open(NavUtils.goToDocumentCenter(rowProjectId || projectId, folderId));
		}
	};
	return (
		<div className={styles.row}>
			<div className={styles.row__information}>
				<div className={styles["row__information--leftSide"]} data-testid={`information_row_${informationId}`}>
					{isCheckboxDisplayed && (
						<Checkbox
							checked={isChecked}
							color="primary"
							data-testid={`check_information_row_${informationId}`}
							size="medium"
							onChange={(e) => {
								onCheck(e.target.checked, row);
							}}
						/>
					)}
					<span className={styles.row__title}>
						{informationType === SENTENCE_TYPE.INFORMATION ||
						(!isSegFeatureEnabled(SegFlags.PROJECT_REQUIREMENTS) && !isTemplate) ? (
							<>
								<IconComponent
									className={styles.infoIcon}
									color="var(--grey-500)"
									icon={icon.faStream}
								/>
								{`INFO_${informationId}`}
								{coverageStatus && (
									<Chip
										className={(covered && styles.coverage__covered) || styles.coverage__toCover}
										label={
											(covered &&
												translate("common:children-requirements.coverage-status.covered")) ||
											translate("common:children-requirements.coverage-status.to-cover")
										}
									/>
								)}
							</>
						) : (
							<>
								<IconComponent
									className={styles.requiredIcon}
									color="var(--red-500)"
									icon={icon.faAsterisk}
								/>
								{`REQ_${informationId}`}
								{coverageStatus && (
									<Chip
										className={(covered && styles.coverage__covered) || styles.coverage__toCover}
										label={
											(covered &&
												translate("common:children-requirements.coverage-status.covered")) ||
											translate("common:children-requirements.coverage-status.to-cover")
										}
									/>
								)}
							</>
						)}
					</span>
					<VerticalDivider className={styles.divider} />
					{hasFolderIcon && (
						<CustomTooltip title={hierarchy}>
							<CustomIconButton
								data-size="xs"
								icon={icon.faFolderTree}
								size="xs"
								variant="outlined"
								onClick={handleOpenDocumentCenter}
								onMouseOver={handleRetrieveHierarchy}
							/>
						</CustomTooltip>
					)}
					<Hierarchy
						enableFirstLocationCut
						isDangerousSetInnerHTML
						className={styles.breadcrumbContainer}
						documentName={documentName}
						hierarchy={location}
					/>
				</div>
				<div className={styles["row__information--actions"]}>
					{DISPLAY_SCORE && (
						<span className={styles["row__information--score"]}>
							{(score === 0 || score) && `${score}%`}
						</span>
					)}
					<CustomIconButton
						data-testid={`file.icon.btn_${informationId}`}
						icon={icon.faFile}
						size="sm"
						tooltip={translate("common:btn.display")}
						variant="outlined"
						onClick={handleClickDisplay}
					/>
				</div>
			</div>
			{isNonEmptyArray(categories) && (
				<div className={styles.row__attributes}>
					<CategoryRowRiskLevel categories={categories} containerId={informationId} />
				</div>
			)}
			<p className={styles.row__description}>
				{content ? (
					dangerousSetInnerHTML({ text: content })
				) : (
					<>{translate("common:searchcard.image-matched")}</>
				)}
			</p>
		</div>
	);
}
